import { useState, useEffect, useRef } from 'react';
import { SchoolData } from '@/lib/onboarding-store';
import { WizardStepLayout } from './WizardStepLayout';
import { Search, Loader2, ChevronDown, MapPin } from 'lucide-react';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { lookupSchoolsByName, SchoolSuggestion } from '@/lib/api';
import { isDemoMode } from '@/lib/demo/store';

interface SchoolStep1Props {
  data: Partial<SchoolData>;
  onChange: (field: keyof SchoolData, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

/**
 * In demo mode the school is a fixture, not something to look up. The live
 * lookup offered a dropdown whose only outcomes were "No schools found" and the
 * manual-entry escape hatch — a dead end dressed up as a choice, on the very
 * first screen of the demo. Prefilling the whole identity block skips it.
 *
 * The postcode comes along deliberately: it is a required field whose only
 * auto-fill path was selecting from that dropdown, so removing the dropdown
 * without it would leave the demo user typing one by hand.
 */
const DEMO_PREFILL_KEY = 'cmc_demo_school_prefilled';
const DEMO_SCHOOL = { name: 'Example School', postcode: 'BR2 9JE', address: 'Bromley' } as const;

const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

function parsePhoneLocal(value: string): string {
  // Strip country code prefix (e.g. "+44 ") and return only digits
  return value.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
}

function getErrors(data: Partial<SchoolData>): string[] {
  const errors: string[] = [];
  if (!data.schoolName?.trim()) errors.push('School name is required.');
  if (!data.postcode?.trim()) {
    errors.push('Postcode is required.');
  } else if (!UK_POSTCODE_RE.test(data.postcode.trim())) {
    errors.push('Postcode doesn\'t look like a valid UK postcode (e.g. SW1A 1AA).');
  }
  if (data.phone) {
    const local = parsePhoneLocal(data.phone);
    const isUK = data.phone.startsWith('+44');
    // Treat dial-code-only ("+44 " with no digits) as empty — optional, so skip silently.
    // Treat any digits present but wrong count as an error.
    if (local.length > 0 && (isUK ? local.length !== 10 : local.length < 7)) {
      errors.push(isUK ? 'School phone must be exactly 10 digits after +44.' : 'School phone number is invalid.');
    }
  }
  return errors;
}

export function SchoolStep1Verify({ data, onChange, onNext, onBack, onReturnToReview }: SchoolStep1Props) {
  const [submitted, setSubmitted] = useState(false);
  const errors = getErrors(data);
  const showError = (field: 'schoolName' | 'postcode') =>
    submitted && errors.some(e => e.toLowerCase().includes(field === 'schoolName' ? 'school name' : 'postcode'));

  // School search state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SchoolSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When the user picks a school from the dropdown we set searchQuery to the
  // chosen name. That assignment would otherwise re-trigger the search effect
  // below and re-open the dropdown 400ms later. Tag the value we just set so
  // the search effect can skip it.
  const skipNextSearchRef = useRef<string | null>(null);

  const demo = isDemoMode();

  // Seed search field from existing school name
  useEffect(() => {
    if (demo) {
      // Prefill ONCE per tab, not once per mount. This component remounts every
      // time the wizard navigates back to step 1, so an unconditional overwrite
      // here would silently wipe a name the user had edited and then come back
      // to correct. The flag lives in sessionStorage so a mid-wizard reload
      // doesn't reopen the same hole.
      let alreadyPrefilled = false;
      try { alreadyPrefilled = sessionStorage.getItem(DEMO_PREFILL_KEY) === '1'; } catch { /* private browsing */ }
      if (!alreadyPrefilled) {
        setSearchQuery(DEMO_SCHOOL.name);
        onChange('schoolName', DEMO_SCHOOL.name);
        if (!data.postcode) onChange('postcode', DEMO_SCHOOL.postcode);
        if (!data.address) onChange('address', DEMO_SCHOOL.address);
        try { sessionStorage.setItem(DEMO_PREFILL_KEY, '1'); } catch { /* quota */ }
      } else if (data.schoolName) {
        setSearchQuery(data.schoolName);
      }
      return;
    }
    if (data.schoolName && !searchQuery) {
      setSearchQuery(data.schoolName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // No lookup in demo — there is no real register behind it.
    if (demo) return;

    // If the current value matches what we just programmatically set via
    // selectSchool, swallow this run — the dropdown was just dismissed and
    // re-searching for it would immediately reopen it.
    if (skipNextSearchRef.current !== null && skipNextSearchRef.current === searchQuery) {
      skipNextSearchRef.current = null;
      return;
    }

    if (searchQuery.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      const results = await lookupSchoolsByName(searchQuery.trim());
      setSuggestions(results);
      setIsLoading(false);
      setShowDropdown(true);
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const selectSchool = (school: SchoolSuggestion | null) => {
    if (school) {
      // Tag the value we're about to set so the search effect knows to skip
      // this round-trip. Without this, the effect re-fires, the API returns
      // the same school, and the dropdown re-opens 400ms after selection.
      skipNextSearchRef.current = school.name;
      setSearchQuery(school.name);
      onChange('schoolName', school.name);
      if (school.postcode) onChange('postcode', school.postcode);
      if (school.town) onChange('address', school.town);
    }
    setShowDropdown(false);
    setSuggestions([]);
  };

  return (
    <WizardStepLayout
      currentStep={1}
      totalSteps={8}
      title="School Verification"
      subtitle="Let's set up your school on CoverMyClass"
      onNext={onNext}
      onBack={onBack}
      showBack={true}
      validationErrors={errors}
      onSubmitAttempt={() => setSubmitted(true)}
      onReturnToReview={onReturnToReview}
    >
      <div className="space-y-6">

        {/* School name search — drives the lookup */}
        <div className="space-y-2" ref={dropdownRef}>
          <label className="flex items-center text-sm font-semibold text-foreground">
            School Name <span className="text-destructive ml-1">*</span>
            <HelpTooltip text="Start typing your school name and select it from the list. This will auto-fill your postcode. You can also type it manually below." />
          </label>
          <div className="relative">
            {/* No z-index. The sticky wizard header is z-10, and an equal z-index in
                the same stacking context is resolved by DOM order — so a field icon
                at z-10 painted OVER the progress banner once the field scrolled
                under it. Positioned siblings already paint above the input, so the
                z-10 bought nothing and cost that. */}
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                onChange('schoolName', e.target.value);
              }}
              placeholder="e.g., Oakwood Academy"
              autoComplete="off"
              className={`input-premium pl-12 pr-10 ${showError('schoolName') ? 'border-destructive focus:border-destructive' : ''}`}
            />
            {isLoading ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
            ) : (!demo && suggestions.length > 0) ? (
              <button
                type="button"
                onClick={() => setShowDropdown(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>
            ) : null}

            {/* Suggestions dropdown */}
            {!demo && showDropdown && (searchQuery.length >= 3) && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
                {suggestions.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">No schools found, try a different name.</div>
                ) : (
                  <ul className="max-h-64 overflow-y-auto divide-y divide-border/50">
                    {suggestions.map((school, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          onClick={() => selectSchool(school)}
                          className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="font-medium text-sm text-foreground">{school.name}</div>
                          {(school.town || school.postcode) && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              {[school.town, school.postcode].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {/* Always-visible "Other" escape hatch */}
                <button
                  type="button"
                  onClick={() => selectSchool(null)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-t border-border text-sm text-muted-foreground font-medium"
                >
                  My school isn't listed. I'll enter details manually
                </button>
              </div>
            )}
          </div>
          {!demo && !isLoading && searchQuery.length >= 3 && !showDropdown && suggestions.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {suggestions.length} match{suggestions.length !== 1 ? 'es' : ''} found:{' '}
              <button type="button" onClick={() => setShowDropdown(true)} className="text-primary hover:underline">
                show suggestions
              </button>
            </p>
          )}
        </div>

        {/* Postcode */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-foreground">
            Postcode <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={data.postcode || ''}
            onChange={(e) => onChange('postcode', e.target.value.toUpperCase())}
            placeholder="SW1A 1AA"
            className={`input-premium uppercase ${showError('postcode') ? 'border-destructive focus:border-destructive' : ''}`}
          />
          <p className="text-xs text-muted-foreground">Auto-filled when you select a school above, or enter manually.</p>
        </div>

        {/* Phone (optional) */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-foreground">
            School Phone Number <span className="text-muted-foreground text-xs">(optional)</span>
          </label>
          <PhoneInput
            value={data.phone || ''}
            onChange={(v) => onChange('phone', v)}
            placeholder="02012345678"
          />
        </div>
      </div>
    </WizardStepLayout>
  );
}
